// PDF report rendered with @react-pdf/renderer.
// Layout adapted from web app PipelineAnalysis (verdict + 4 components),
// using the DIDI palette but rebuilt with PDF primitives (no DOM reuse).
import React from 'react';
import {
  Document, Page, Text, View, StyleSheet, Font, Svg, Rect, LinearGradient, Stop, Defs,
} from '@react-pdf/renderer';
import type { TFunction } from 'i18next';
import i18n from '../../i18n';

// Register Inter from local /public/fonts (served by nginx) — required for
// proper Romanian diacritics (ă â î ș ț) which Helvetica built-in doesn't ship.
// react-pdf's font fetcher needs an absolute URL in the browser, so we
// resolve it against the current page origin at module-eval time.
const FONT_BASE =
  typeof window !== 'undefined' && window.location?.origin
    ? `${window.location.origin}/fonts`
    : '/fonts';

try {
  Font.register({
    family: 'Inter',
    fonts: [
      { src: `${FONT_BASE}/Inter-Regular.ttf`, fontWeight: 400 },
      { src: `${FONT_BASE}/Inter-Italic.ttf`, fontWeight: 400, fontStyle: 'italic' },
      { src: `${FONT_BASE}/Inter-SemiBold.ttf`, fontWeight: 600 },
      { src: `${FONT_BASE}/Inter-Bold.ttf`, fontWeight: 700 },
    ],
  });
  // Disable hyphenation so Inter renders Romanian words intact.
  Font.registerHyphenationCallback((word: string) => [word]);
} catch (e) {
  console.warn('[AnalysisReportPDF] Inter font registration failed:', e);
}
import type {
  AnalysisSession, VerdictResult, TechniquesResult, ClaimsResult,
  AiTamperedResult, SourceAssessmentResult, ClaimVerified, TechniqueDetected,
  AiIndicatorDetected, VerdictSummary, KeyFinding,
} from '../../types/analysis-session';
import type { TechniqueDefinition } from '../../services/technique-definitions.service';

// =============================================================================
// PALETTE — pulled from src/theme/colors.ts, kept inline so PDF rendering
// doesn't touch React component theme (which assumes DOM/CSS).
// =============================================================================
const PALETTE = {
  // Brand
  brandPrimary: '#0052CC',
  brandTeal: '#00BFA6',
  brandViolet: '#7c3aed',
  // Backgrounds
  bg: '#ffffff',
  cardBg: '#F5F7FA',
  cardBorder: '#E5E7EB',
  divider: '#D1D5DB',
  // Text
  textPrimary: '#1F2933',
  textMuted: '#6B7280',
  textSubtle: '#9CA3AF',
  // Risk
  riskCritical: '#B91C1C',
  riskHigh: '#EF4444',
  riskMedium: '#F97316',
  riskLow: '#EAB308',
  riskSafe: '#22C55E',
  // Stance / status
  supports: '#22C55E',
  contradicts: '#EF4444',
  neutral: '#6B7280',
};

// Map risk_category_color (backend value) → palette
function riskColor(categoryColor?: string): string {
  switch ((categoryColor || '').toLowerCase()) {
    case 'darkred': return PALETTE.riskCritical;
    case 'red':     return PALETTE.riskHigh;
    case 'orange':  return PALETTE.riskMedium;
    case 'yellow':  return PALETTE.riskLow;
    case 'green':
    case 'lightgreen': return PALETTE.riskSafe;
    default:        return PALETTE.textMuted;
  }
}

function scoreColor(score: number): string {
  if (score >= 80) return PALETTE.riskCritical;
  if (score >= 60) return PALETTE.riskHigh;
  if (score >= 40) return PALETTE.riskMedium;
  if (score >= 20) return PALETTE.riskLow;
  return PALETTE.riskSafe;
}

function humanize(s: string | null | undefined): string {
  if (!s) return '';
  return s.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch { return iso; }
}

function fmtDuration(ms: number | null | undefined): string {
  if (!ms) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function safeHostname(url: string | null | undefined): string {
  if (!url) return '—';
  try { return new URL(url).hostname; } catch { return url.substring(0, 60); }
}

/** Soften LLM jargon in free text. Backend evidence/reasoning sometimes leaks
 * raw enum tokens like `content_type.clickbait` or `LIKELY_AI` into prose.
 * We humanize only those — the surrounding sentence stays untouched.
 *  - dotted enum (foo.bar_baz) → "Bar baz"  (we drop the namespace prefix)
 *  - SCREAMING_SNAKE (LIKELY_AI) → "Likely Ai"
 *  - lower_snake_case (no_source_attribution) → "No source attribution"
 */
function softenJargon(s: string | null | undefined): string {
  if (!s) return '';
  return s
    // dotted: word_word.word_word — drop the namespace, humanize the tail.
    // First segment MUST allow underscores (e.g. "content_type.clickbait").
    .replace(/\b[a-z][a-z0-9_]*\.[a-z][a-z0-9_]*\b/gi, (m) => {
      const tail = m.split('.').pop() || m;
      return humanize(tail);
    })
    // SCREAMING_SNAKE_CASE
    .replace(/\b[A-Z][A-Z0-9]+(?:_[A-Z0-9]+)+\b/g, (m) => humanize(m))
    // lower_snake_case (2+ chars per part)
    .replace(/\b[a-z]{2,}(?:_[a-z]{2,})+\b/g, (m) => humanize(m));
}

// =============================================================================
// STYLES — flat objects, react-pdf's StyleSheet supports a CSS subset only.
// =============================================================================
const styles = StyleSheet.create({
  page: {
    paddingTop: 48,
    paddingBottom: 56,
    paddingHorizontal: 40,
    fontFamily: 'Inter',
    fontSize: 10,
    color: PALETTE.textPrimary,
    backgroundColor: PALETTE.bg,
  },

  // ── Header ─────────────────────────────────────
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 28,
    paddingBottom: 16,
    borderBottomWidth: 0.5,
    borderBottomColor: PALETTE.cardBorder,
  },
  headerLogoBlock: {
    flexDirection: 'row',
    alignItems: 'flex-end',
  },
  brandName: {
    fontSize: 28,
    fontFamily: 'Inter', fontWeight: 700,
    color: PALETTE.textPrimary,
    letterSpacing: -1.2,
    lineHeight: 1.0,
  },
  brandTagBlock: {
    marginLeft: 4,
    paddingLeft: 10,
    borderLeftWidth: 1,
    borderLeftColor: PALETTE.cardBorder,
  },
  brandTagLine1: {
    fontSize: 8,
    color: PALETTE.textMuted,
    fontFamily: 'Inter', fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 1.2,
  },
  brandTagLine2: {
    fontSize: 9,
    color: PALETTE.textSubtle,
    marginTop: 1,
  },
  reportMeta: { textAlign: 'right' },
  reportLabel: {
    fontSize: 7,
    color: PALETTE.textSubtle,
    textTransform: 'uppercase',
    letterSpacing: 1.4,
    fontFamily: 'Inter', fontWeight: 700,
  },
  reportSession: {
    fontSize: 9,
    fontFamily: 'Inter', fontWeight: 700,
    color: PALETTE.textPrimary,
    marginTop: 2,
    letterSpacing: -0.2,
  },
  reportDate: {
    fontSize: 8,
    color: PALETTE.textMuted,
    marginTop: 2,
  },
  // Thin gradient bar under the divider — a subtle modern accent.
  headerAccent: {
    height: 3,
    marginBottom: 18,
    borderRadius: 2,
  },

  // ── Section ────────────────────────────────────
  section: { marginBottom: 20 },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    marginTop: 4,
  },
  sectionAccent: {
    width: 4,
    height: 18,
    borderRadius: 2,
    marginRight: 10,
  },
  sectionTitle: {
    fontSize: 15,
    fontFamily: 'Inter', fontWeight: 700,
    color: PALETTE.textPrimary,
    letterSpacing: -0.5,
  },
  sectionSubtitle: {
    fontSize: 10,
    fontFamily: 'Inter', fontWeight: 700,
    color: PALETTE.textMuted,
    marginTop: 8,
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },

  // ── Verdict hero ───────────────────────────────
  verdictHero: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FAFBFC',
    borderRadius: 10,
    padding: 22,
    marginBottom: 18,
    borderWidth: 0.5,
    borderColor: PALETTE.cardBorder,
    position: 'relative',
  },
  verdictAccentBar: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 5,
    borderTopLeftRadius: 10,
    borderBottomLeftRadius: 10,
  },
  verdictScoreBox: {
    width: 110,
    alignItems: 'center',
    justifyContent: 'center',
    paddingLeft: 6,
  },
  verdictScoreNum: {
    fontSize: 56,
    fontFamily: 'Inter', fontWeight: 700,
    color: PALETTE.riskHigh,
    lineHeight: 1.0,
    letterSpacing: -2.5,
  },
  verdictScoreLbl: {
    fontSize: 7,
    color: PALETTE.textMuted,
    marginTop: 4,
    textTransform: 'uppercase',
    letterSpacing: 1.4,
    fontFamily: 'Inter', fontWeight: 700,
  },
  verdictDivider: {
    width: 0.5,
    height: 80,
    backgroundColor: PALETTE.cardBorder,
    marginHorizontal: 20,
  },
  verdictBody: { flex: 1 },
  verdictPill: {
    alignSelf: 'flex-start',
    fontSize: 8,
    fontFamily: 'Inter', fontWeight: 700,
    color: '#FFFFFF',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 8,
  },
  verdictCategory: {
    fontSize: 18,
    fontFamily: 'Inter', fontWeight: 700,
    color: PALETTE.textPrimary,
    marginBottom: 4,
    letterSpacing: -0.5,
  },
  verdictMeta: {
    fontSize: 9,
    color: PALETTE.textMuted,
    marginBottom: 10,
  },
  verdictTldr: {
    fontSize: 11,
    color: PALETTE.textPrimary,
    lineHeight: 1.55,
  },

  // ── Action callout ─────────────────────────────
  actionBox: {
    backgroundColor: '#FEF2F2',
    borderLeftWidth: 4,
    borderLeftColor: PALETTE.riskHigh,
    padding: 12,
    borderRadius: 4,
    marginBottom: 14,
  },
  actionEyebrow: {
    fontSize: 8,
    fontFamily: 'Inter', fontWeight: 700,
    color: PALETTE.riskHigh,
    textTransform: 'uppercase',
    letterSpacing: 1.2,
    marginBottom: 4,
  },
  actionHeadline: {
    fontSize: 12,
    fontFamily: 'Inter', fontWeight: 700,
    color: PALETTE.textPrimary,
    marginBottom: 4,
  },
  actionText: {
    fontSize: 10,
    color: PALETTE.textPrimary,
    lineHeight: 1.5,
  },

  // ── Score row (per-component breakdown) ────────
  scoreRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  scoreLabel: {
    width: 110,
    fontSize: 10,
    color: PALETTE.textPrimary,
  },
  scoreBarBg: {
    flex: 1,
    height: 8,
    backgroundColor: PALETTE.cardBorder,
    borderRadius: 4,
    marginHorizontal: 8,
  },
  scoreBarFill: {
    height: 8,
    borderRadius: 4,
  },
  scoreNum: {
    width: 36,
    fontSize: 10,
    fontFamily: 'Inter', fontWeight: 700,
    color: PALETTE.textPrimary,
    textAlign: 'right',
  },

  // ── Stat grid ──────────────────────────────────
  statGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 8,
  },
  statBox: {
    width: '25%',
    paddingRight: 8,
    marginBottom: 8,
  },
  statLabel: {
    fontSize: 8,
    color: PALETTE.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  statValue: {
    fontSize: 14,
    fontFamily: 'Inter', fontWeight: 700,
    color: PALETTE.textPrimary,
    marginTop: 2,
  },

  // ── Card list (techniques / claims / indicators) ──
  card: {
    backgroundColor: PALETTE.cardBg,
    borderRadius: 4,
    padding: 10,
    marginBottom: 6,
    borderLeftWidth: 3,
    borderLeftColor: PALETTE.brandPrimary,
  },
  cardTitleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 4,
  },
  cardTitle: {
    fontSize: 10,
    fontFamily: 'Inter', fontWeight: 700,
    color: PALETTE.textPrimary,
    flex: 1,
    paddingRight: 8,
    lineHeight: 1.35,
  },
  cardBadge: {
    fontSize: 7.5,
    fontFamily: 'Inter', fontWeight: 700,
    color: PALETTE.bg,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 3,
    flexShrink: 0,
    maxWidth: 110,
    textAlign: 'center',
  },
  cardMeta: {
    fontSize: 8,
    color: PALETTE.textMuted,
    marginBottom: 4,
  },
  cardEvidence: {
    fontSize: 9,
    color: PALETTE.textPrimary,
    fontStyle: 'italic',
    lineHeight: 1.4,
  },

  // ── Source row ─────────────────────────────────
  sourceRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginTop: 4,
    marginLeft: 8,
  },
  sourceStance: {
    width: 60,
    fontSize: 7,
    fontFamily: 'Inter', fontWeight: 700,
    textTransform: 'uppercase',
  },
  sourceUrl: {
    flex: 1,
    fontSize: 8,
    color: PALETTE.textMuted,
  },
  sourceQuote: {
    fontSize: 8,
    color: PALETTE.textPrimary,
    fontStyle: 'italic',
    marginLeft: 8,
    marginTop: 2,
  },

  // ── Input preview ──────────────────────────────
  inputBox: {
    backgroundColor: PALETTE.cardBg,
    borderRadius: 4,
    padding: 10,
    marginBottom: 14,
  },
  inputLabel: {
    fontSize: 8,
    color: PALETTE.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 4,
  },
  inputContent: {
    fontSize: 10,
    color: PALETTE.textPrimary,
    lineHeight: 1.5,
  },

  // ── Footer ─────────────────────────────────────
  footer: {
    position: 'absolute',
    bottom: 24,
    left: 40,
    right: 40,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingTop: 8,
    borderTopWidth: 0.5,
    borderTopColor: PALETTE.cardBorder,
  },
  footerText: {
    fontSize: 8,
    color: PALETTE.textSubtle,
  },

  // ── Generic helpers ────────────────────────────
  row: { flexDirection: 'row' },
  textMuted: { color: PALETTE.textMuted },
  small: { fontSize: 8 },
  mb4: { marginBottom: 4 },
  mb8: { marginBottom: 8 },
  mb12: { marginBottom: 12 },
  bold: { fontFamily: 'Inter', fontWeight: 700 },
  italic: { fontStyle: 'italic' },
});

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

// Brand mark: lowercase "didi" — matches the web app logo styling
// (Inter Bold, tight letter-spacing, no decorative dot).
const BrandMark: React.FC = () => (
  <View style={styles.headerLogoBlock}>
    <Text style={styles.brandName}>didi</Text>
    <View style={styles.brandTagBlock}>
      <Text style={styles.brandTagLine1}>Analysis</Text>
      <Text style={styles.brandTagLine2}>Report</Text>
    </View>
  </View>
);

// Subtle gradient accent bar — sits right under the header divider.
const HeaderAccent: React.FC = () => (
  <View style={styles.headerAccent}>
    <Svg width="100%" height={3} viewBox="0 0 600 3" preserveAspectRatio="none">
      <Defs>
        <LinearGradient id="accent" x1="0" y1="0" x2="600" y2="0" gradientUnits="userSpaceOnUse">
          <Stop offset="0" stopColor={PALETTE.brandPrimary} />
          <Stop offset="0.5" stopColor={PALETTE.brandViolet} />
          <Stop offset="1" stopColor={PALETTE.brandTeal} />
        </LinearGradient>
      </Defs>
      <Rect x="0" y="0" width="600" height="3" fill="url(#accent)" />
    </Svg>
  </View>
);

const ReportHeader: React.FC<{ session: AnalysisSession }> = ({ session }) => (
  <>
    <View style={styles.header} fixed>
      <BrandMark />
      <View style={styles.reportMeta}>
        <Text style={styles.reportLabel}>Session</Text>
        <Text style={styles.reportSession}>{session.session_id.substring(0, 18)}…</Text>
        <Text style={styles.reportDate}>Generated: {new Date().toLocaleString()}</Text>
      </View>
    </View>
    <HeaderAccent />
  </>
);

// SectionHeader: colored accent bar + title — visually groups each component
// so the reader scans the report as 4 distinct chapters.
const SectionHeader: React.FC<{ title: string; accent: string }> = ({ title, accent }) => (
  <View style={styles.sectionHeader}>
    <View style={[styles.sectionAccent, { backgroundColor: accent }]} />
    <Text style={styles.sectionTitle}>{title}</Text>
  </View>
);

const ReportFooter: React.FC = () => (
  <View style={styles.footer} fixed>
    <Text style={styles.footerText}>
      <Text style={{ fontFamily: 'Inter', fontWeight: 700, color: PALETTE.textPrimary, letterSpacing: -0.5 }}>didi</Text>
      {'  ·  '}didi365.eu  ·  Misinformation detection
    </Text>
    <Text
      style={styles.footerText}
      render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
    />
  </View>
);

const VerdictHero: React.FC<{
  verdict: VerdictResult;
  isRo: boolean;
  t: TFunction;
}> = ({ verdict, isRo, t }) => {
  const color = riskColor(verdict.risk_category_color);
  const summary = (verdict.context_summary as { verdict_summary?: VerdictSummary } | undefined)?.verdict_summary;
  const tldr = summary ? (isRo ? summary.tl_dr_ro : summary.tl_dr_en) : null;

  return (
    <View style={styles.verdictHero}>
      <View style={[styles.verdictAccentBar, { backgroundColor: color }]} />
      <View style={styles.verdictScoreBox}>
        <Text style={[styles.verdictScoreNum, { color }]}>{verdict.risk_score}</Text>
        <Text style={styles.verdictScoreLbl}>{t('analysis.pdf.riskScoreSuffix')}</Text>
      </View>
      <View style={styles.verdictDivider} />
      <View style={styles.verdictBody}>
        <Text style={[styles.verdictPill, { backgroundColor: color }]}>
          {t('analysis.pdf.verdictTitle')}
        </Text>
        <Text style={[styles.verdictCategory, { color }]}>
          {humanize(verdict.risk_category)}
        </Text>
        <Text style={styles.verdictMeta}>
          {humanize(verdict.severity)}
          {verdict.confidence != null && `   ·   ${t('analysis.pdf.confidenceLabel')} ${verdict.confidence}%`}
          {verdict.recommended_action && `   ·   ${humanize(verdict.recommended_action)}`}
        </Text>
        {tldr && <Text style={styles.verdictTldr}>{softenJargon(tldr)}</Text>}
      </View>
    </View>
  );
};

const ActionCallout: React.FC<{ summary: VerdictSummary; isRo: boolean; accent: string; t: TFunction }> = ({
  summary, isRo, accent, t,
}) => {
  const text = isRo ? summary.what_to_do_ro : summary.what_to_do_en;
  if (!text) return null;
  const sev = summary.key_findings?.[0]?.severity;
  const eyebrow = sev === 'critical'
    ? t('analysis.pdf.actionDoNotShare')
    : sev === 'warning'
      ? t('analysis.pdf.actionReadCritically')
      : t('analysis.pdf.actionNote');
  const headline = sev === 'critical'
    ? t('analysis.pdf.actionDoNotShareHeadline')
    : t('analysis.pdf.actionReadCarefullyHeadline');
  return (
    <View style={[styles.actionBox, { borderLeftColor: accent, backgroundColor: `${accent}15` }]} wrap={false}>
      <Text style={[styles.actionEyebrow, { color: accent }]}>{eyebrow}</Text>
      <Text style={styles.actionHeadline}>{headline}</Text>
      <Text style={styles.actionText}>{softenJargon(text)}</Text>
    </View>
  );
};

const KeyFindingsList: React.FC<{ findings: KeyFinding[]; isRo: boolean; t: TFunction }> = ({ findings, isRo, t }) => {
  if (!findings.length) return null;
  return (
    <View style={styles.section}>
      <Text style={styles.sectionSubtitle}>{t('analysis.pdf.keyFindings')}</Text>
      {findings.map((f, i) => {
        const accent =
          f.severity === 'critical' ? PALETTE.riskHigh :
          f.severity === 'warning' ? PALETTE.riskMedium : PALETTE.brandPrimary;
        return (
          <View key={i} style={[styles.card, { borderLeftColor: accent }]} wrap={false}>
            <View style={styles.cardTitleRow}>
              <Text style={styles.cardTitle}>{softenJargon(isRo ? f.ro : f.en)}</Text>
              <Text style={[styles.cardBadge, { backgroundColor: accent }]}>
                {f.severity.toUpperCase()}
              </Text>
            </View>
            {f.evidence_ref?.quote && (
              <Text style={styles.cardEvidence}>"{softenJargon(f.evidence_ref.quote)}"</Text>
            )}
          </View>
        );
      })}
    </View>
  );
};

const ScoreBreakdown: React.FC<{ verdict: VerdictResult; t: TFunction }> = ({ verdict, t }) => {
  const entries: { name: string; value: number | null }[] = [
    { name: t('analysis.pdf.scoreManipulation'), value: verdict.score_manipulation },
    { name: t('analysis.pdf.scoreClaims'), value: verdict.score_claims },
    { name: t('analysis.pdf.scoreAi'), value: verdict.score_ai },
    { name: t('analysis.pdf.scoreSource'), value: verdict.score_source },
  ];
  return (
    <View style={styles.section}>
      <Text style={styles.sectionSubtitle}>{t('analysis.pdf.scoreBreakdown')}</Text>
      {entries.map((e, i) => {
        const v = e.value;
        return (
          <View key={i} style={styles.scoreRow}>
            <Text style={styles.scoreLabel}>{e.name}</Text>
            <View style={styles.scoreBarBg}>
              {v != null && (
                <View style={[
                  styles.scoreBarFill,
                  { width: `${Math.min(100, Math.max(0, v))}%`, backgroundColor: scoreColor(v) },
                ]} />
              )}
            </View>
            <Text style={styles.scoreNum}>{v != null ? Math.round(v) : '—'}</Text>
          </View>
        );
      })}
    </View>
  );
};

const InputPreview: React.FC<{ session: AnalysisSession; t: TFunction }> = ({ session, t }) => {
  let label = t('analysis.pdf.analyzedContent');
  let content = '';
  if (session.input_text) {
    content = session.input_text.length > 600 ? session.input_text.substring(0, 600) + '…' : session.input_text;
  } else if (session.input_url) {
    label = 'URL';
    content = session.input_url;
  } else if (session.input_media_url) {
    label = t('analysis.pdf.mediaFile');
    content = safeHostname(session.input_media_url) + ' — ' + session.input_type.toUpperCase();
  }
  if (!content) return null;
  return (
    <View style={styles.inputBox}>
      <Text style={styles.inputLabel}>{label}</Text>
      <Text style={styles.inputContent}>{content}</Text>
    </View>
  );
};

const TechniquesSection: React.FC<{
  data: TechniquesResult; isRo: boolean; techDefs: TechniqueDefinition[]; t: TFunction;
}> = ({ data, isRo, techDefs, t }) => {
  const techs = data.techniques_detected || [];
  const findDef = (rawName: string | undefined): TechniqueDefinition | undefined => {
    if (!rawName || !techDefs?.length) return undefined;
    return techDefs.find(d => d.technique_name === rawName);
  };
  return (
    <View style={styles.section} break>
      <SectionHeader
        title={t('analysis.pdf.techniquesTitle')}
        accent={scoreColor(data.manipulation_score)}
      />
      <View style={styles.statGrid}>
        <View style={styles.statBox}>
          <Text style={styles.statLabel}>{t('analysis.pdf.statScore')}</Text>
          <Text style={[styles.statValue, { color: scoreColor(data.manipulation_score) }]}>
            {data.manipulation_score}/100
          </Text>
        </View>
        <View style={styles.statBox}>
          <Text style={styles.statLabel}>{t('analysis.pdf.statTechniques')}</Text>
          <Text style={styles.statValue}>{data.techniques_count}</Text>
        </View>
        <View style={styles.statBox}>
          <Text style={styles.statLabel}>{t('analysis.pdf.statDimensions')}</Text>
          <Text style={styles.statValue}>{data.dimensions_affected?.length || 0}</Text>
        </View>
        <View style={styles.statBox}>
          <Text style={styles.statLabel}>{t('analysis.pdf.statDuration')}</Text>
          <Text style={styles.statValue}>{fmtDuration(data.total_duration_ms)}</Text>
        </View>
      </View>

      {techs.length > 0 ? (
        <>
          <Text style={[styles.sectionSubtitle, { marginTop: 8 }]}>
            {t('analysis.pdf.detectedTechniques')}
          </Text>
          {techs.map((tech: TechniqueDetected, i: number) => {
            const sevColor = scoreColor(tech.severity);
            const rawName = (isRo ? tech.technique_name_ro : tech.technique_name_en) || tech.technique_name || tech.name;
            const name = softenJargon(rawName);
            const dim = (isRo ? tech.dimension_name_ro : tech.dimension_name_en) || tech.dimension_name || tech.dimension;
            const def = findDef(tech.name);
            const description = def ? (isRo ? def.description_ro : def.description_en) : undefined;
            return (
              <View key={i} style={[styles.card, { borderLeftColor: sevColor }]} wrap={false}>
                <View style={styles.cardTitleRow}>
                  <Text style={styles.cardTitle}>{name}</Text>
                  <Text style={[styles.cardBadge, { backgroundColor: sevColor }]}>
                    SEV {tech.severity}
                  </Text>
                </View>
                <Text style={styles.cardMeta}>
                  {humanize(dim)} · {t('analysis.pdf.confidenceShort')} {tech.confidence}% · {t('analysis.pdf.intensityShort')} {tech.intensity}
                </Text>
                {description && (
                  <Text style={[styles.cardMeta, { fontStyle: 'italic', marginBottom: 4, color: PALETTE.textMuted }]}>
                    {description}
                  </Text>
                )}
                {tech.evidence && <Text style={styles.cardEvidence}>"{softenJargon(tech.evidence)}"</Text>}
              </View>
            );
          })}
        </>
      ) : (
        <Text style={styles.textMuted}>{t('analysis.pdf.noTechniquesPeriod')}</Text>
      )}
    </View>
  );
};

const ClaimsSection: React.FC<{ data: ClaimsResult; isRo: boolean; t: TFunction }> = ({ data, isRo, t }) => {
  const claims = data.claims_verified || [];
  return (
    <View style={styles.section} break>
      <SectionHeader
        title={t('analysis.pdf.claimsTitle')}
        accent={scoreColor(100 - (data.credibility_score || 0))}
      />
      <View style={styles.statGrid}>
        <View style={styles.statBox}>
          <Text style={styles.statLabel}>{t('analysis.pdf.credibilityLabel')}</Text>
          <Text style={[styles.statValue, { color: scoreColor(100 - (data.credibility_score || 0)) }]}>
            {data.credibility_score ?? '—'}/100
          </Text>
        </View>
        <View style={styles.statBox}>
          <Text style={styles.statLabel}>{t('analysis.pdf.totalLabel')}</Text>
          <Text style={styles.statValue}>{data.total_claims}</Text>
        </View>
        <View style={styles.statBox}>
          <Text style={styles.statLabel}>{t('analysis.pdf.verifiedTrueShort')}</Text>
          <Text style={[styles.statValue, { color: PALETTE.supports }]}>{data.verified_true}</Text>
        </View>
        <View style={styles.statBox}>
          <Text style={styles.statLabel}>{t('analysis.pdf.verifiedFalseShort')}</Text>
          <Text style={[styles.statValue, { color: PALETTE.contradicts }]}>{data.verified_false}</Text>
        </View>
      </View>

      {data.interpretation && (
        <View wrap={false}>
          <Text style={[styles.cardEvidence, styles.mb12]}>{softenJargon(data.interpretation)}</Text>
        </View>
      )}

      {claims.length > 0 ? (
        <>
          <Text style={[styles.sectionSubtitle, { marginTop: 8 }]}>
            {t('analysis.pdf.claimsListTitle')}
          </Text>
          {claims.map((claim: ClaimVerified, i: number) => {
            const statusColor =
              claim.status?.toLowerCase().includes('false') ? PALETTE.contradicts :
              claim.status?.toLowerCase().includes('true') ? PALETTE.supports :
              claim.status?.toLowerCase().includes('mislead') ? PALETTE.riskMedium :
              PALETTE.textMuted;
            const statusName = (isRo ? claim.status_name_ro : claim.status_name_en) || claim.status_name || claim.status;
            return (
              <View key={i} style={[styles.card, { borderLeftColor: statusColor }]} wrap={false}>
                <View style={styles.cardTitleRow}>
                  <Text style={styles.cardTitle}>{softenJargon(claim.text)}</Text>
                  <Text style={[styles.cardBadge, { backgroundColor: statusColor }]}>
                    {humanize(statusName).toUpperCase()}
                  </Text>
                </View>
                <Text style={styles.cardMeta}>
                  {humanize(claim.type_name || claim.type)} · {humanize(claim.priority)} · {t('analysis.pdf.confidenceShort')} {claim.confidence}%
                </Text>
                {claim.reasoning && <Text style={styles.cardEvidence}>{softenJargon(claim.reasoning)}</Text>}
                {claim.sources?.slice(0, 4).map((s, j) => (
                  <View key={j} style={styles.sourceRow}>
                    <Text style={[styles.sourceStance, {
                      color: s.stance === 'SUPPORTS' ? PALETTE.supports :
                             s.stance === 'CONTRADICTS' ? PALETTE.contradicts : PALETTE.neutral,
                    }]}>{s.stance}</Text>
                    <Text style={styles.sourceUrl}>{safeHostname(s.url)}</Text>
                  </View>
                ))}
              </View>
            );
          })}
        </>
      ) : (
        <Text style={styles.textMuted}>{t('analysis.pdf.noClaimsExtracted')}</Text>
      )}
    </View>
  );
};

const AiTamperedSection: React.FC<{ data: AiTamperedResult; isRo: boolean; t: TFunction }> = ({ data, isRo, t }) => {
  const indicators = data.indicators_detected || [];
  const aiColor = scoreColor(data.ai_probability);
  return (
    <View style={styles.section} break>
      <SectionHeader
        title={t('analysis.pdf.aiTitle')}
        accent={scoreColor(data.ai_probability)}
      />
      <View style={styles.statGrid}>
        <View style={styles.statBox}>
          <Text style={styles.statLabel}>{t('analysis.pdf.aiProbabilityLabel')}</Text>
          <Text style={[styles.statValue, { color: aiColor }]}>{data.ai_probability}%</Text>
        </View>
        <View style={styles.statBox}>
          <Text style={styles.statLabel}>{t('analysis.pdf.verdictShort')}</Text>
          <Text style={styles.statValue}>{humanize(data.verdict)}</Text>
        </View>
        <View style={styles.statBox}>
          <Text style={styles.statLabel}>{t('analysis.pdf.indicatorsLabel')}</Text>
          <Text style={styles.statValue}>{data.indicators_count}</Text>
        </View>
        <View style={styles.statBox}>
          <Text style={styles.statLabel}>{t('analysis.pdf.disclosureLabel')}</Text>
          <Text style={[styles.statValue, { color: data.disclosure_detected ? PALETTE.supports : PALETTE.textMuted }]}>
            {data.disclosure_detected ? t('analysis.pdf.yes') : t('analysis.pdf.no')}
          </Text>
        </View>
      </View>

      {indicators.length > 0 ? (
        <>
          <Text style={[styles.sectionSubtitle, { marginTop: 8 }]}>
            {t('analysis.pdf.detectedIndicatorsTitle')}
          </Text>
          {indicators.map((ind: AiIndicatorDetected, i: number) => {
            const rawName = (isRo ? ind.indicator_name_ro : ind.indicator_name_en) || ind.indicator_name || ind.name;
            const name = softenJargon(rawName);
            const c = scoreColor(ind.confidence);
            return (
              <View key={i} style={[styles.card, { borderLeftColor: c }]} wrap={false}>
                <View style={styles.cardTitleRow}>
                  <Text style={styles.cardTitle}>{name}</Text>
                  <Text style={[styles.cardBadge, { backgroundColor: c }]}>
                    {ind.confidence}%
                  </Text>
                </View>
                <Text style={styles.cardMeta}>{humanize(ind.category_name || ind.category)}</Text>
                {ind.evidence && <Text style={styles.cardEvidence}>"{softenJargon(ind.evidence)}"</Text>}
              </View>
            );
          })}
        </>
      ) : (
        <Text style={styles.textMuted}>{t('analysis.pdf.noIndicators')}</Text>
      )}
    </View>
  );
};

const SourceSection: React.FC<{ data: SourceAssessmentResult; t: TFunction }> = ({ data, t }) => {
  const trustColor = scoreColor(100 - (data.trust_score || 0));
  return (
    <View style={styles.section} break>
      <SectionHeader
        title={t('analysis.pdf.sourceTitle')}
        accent={scoreColor(100 - (data.trust_score || 0))}
      />
      <View style={styles.statGrid}>
        <View style={styles.statBox}>
          <Text style={styles.statLabel}>{t('analysis.pdf.trustScoreLabel')}</Text>
          <Text style={[styles.statValue, { color: trustColor }]}>{data.trust_score}/100</Text>
        </View>
        <View style={styles.statBox}>
          <Text style={styles.statLabel}>{t('analysis.pdf.verdictShort')}</Text>
          <Text style={styles.statValue}>{humanize(data.verdict)}</Text>
        </View>
        <View style={styles.statBox}>
          <Text style={styles.statLabel}>{t('analysis.pdf.riskLevelShort')}</Text>
          <Text style={styles.statValue}>{humanize(data.risk_level)}</Text>
        </View>
        <View style={styles.statBox}>
          <Text style={styles.statLabel}>{t('analysis.pdf.searchesLabel')}</Text>
          <Text style={styles.statValue}>{data.search_results_count ?? 0}</Text>
        </View>
      </View>

      <Text style={[styles.sectionSubtitle, { marginTop: 8 }]}>
        {t('analysis.pdf.sourceComponents')}
      </Text>
      {data.publication && (
        <View style={styles.card} wrap={false}>
          <View style={styles.cardTitleRow}>
            <Text style={styles.cardTitle}>{t('analysis.pdf.publication')}: {data.publication.name || '—'}</Text>
            <Text style={[styles.cardBadge, { backgroundColor: scoreColor(100 - (data.publication.score || 0)) }]}>
              {data.publication.score}/100
            </Text>
          </View>
          <Text style={styles.cardMeta}>{humanize(data.publication.source_type)} · {data.publication.confirmed ? t('analysis.pdf.confirmedYes') : t('analysis.pdf.confirmedNo')}</Text>
        </View>
      )}
      {data.author && data.author.name && (
        <View style={styles.card} wrap={false}>
          <View style={styles.cardTitleRow}>
            <Text style={styles.cardTitle}>{t('analysis.pdf.author')}: {data.author.name}</Text>
            <Text style={[styles.cardBadge, { backgroundColor: scoreColor(100 - (data.author.score || 0)) }]}>
              {data.author.score}/100
            </Text>
          </View>
          <Text style={styles.cardMeta}>{humanize(data.author.classification)}</Text>
        </View>
      )}
      {data.platform && (
        <View style={styles.card} wrap={false}>
          <View style={styles.cardTitleRow}>
            <Text style={styles.cardTitle}>{t('analysis.pdf.platform')}: {data.platform.name}</Text>
            <Text style={[styles.cardBadge, { backgroundColor: scoreColor(100 - (data.platform.score || 0)) }]}>
              {data.platform.score}/100
            </Text>
          </View>
        </View>
      )}
      {data.domain && (
        <View style={styles.card} wrap={false}>
          <View style={styles.cardTitleRow}>
            <Text style={styles.cardTitle}>{t('analysis.pdf.domainAxis')}: {data.domain.name || '—'}</Text>
            <Text style={[styles.cardBadge, { backgroundColor: scoreColor(100 - (data.domain.score || 0)) }]}>
              {data.domain.score}/100
            </Text>
          </View>
          <Text style={styles.cardMeta}>
            {data.domain.age_days != null && `${t('analysis.pdf.ageLabel')} ${data.domain.age_days} ${t('analysis.pdf.ageDays')} · `}
            {data.domain.has_ssl ? 'SSL OK' : 'No SSL'}
            {data.domain.is_blacklisted && ' · BLACKLISTED'}
          </Text>
        </View>
      )}

      {(data.red_flags?.length || 0) > 0 && (
        <>
          <Text style={[styles.sectionSubtitle, { marginTop: 8, color: PALETTE.riskHigh }]}>
            {t('analysis.pdf.redFlagsLabel')}
          </Text>
          {data.red_flags!.map((f, i) => (
            <Text key={i} style={[styles.cardEvidence, { color: PALETTE.riskHigh }]}>• {f}</Text>
          ))}
        </>
      )}
    </View>
  );
};

// =============================================================================
// MAIN DOCUMENT
// =============================================================================

export interface AnalysisReportPDFProps {
  session: AnalysisSession;
  language?: 'ro' | 'en';
  techDefs?: TechniqueDefinition[];
}

export const AnalysisReportPDF: React.FC<AnalysisReportPDFProps> = ({ session, language, techDefs = [] }) => {
  // Resolve language: explicit prop wins, else use the live i18n state.
  const lang: 'ro' | 'en' = language
    ? language
    : ((i18n.language || '').toLowerCase().startsWith('ro') ? 'ro' : 'en');
  const isRo = lang === 'ro';
  // Bind a `t` function to the resolved language for this PDF render.
  const t: TFunction = (i18n.getFixedT(lang) as unknown) as TFunction;
  const verdict = session.verdict;
  const summary = (verdict?.context_summary as { verdict_summary?: VerdictSummary } | undefined)?.verdict_summary;
  const accent = riskColor(verdict?.risk_category_color);

  return (
    <Document
      title={`didi · Analysis Report — ${session.session_id}`}
      author="didi"
      subject="Misinformation Analysis Report"
    >
      <Page size="A4" style={styles.page}>
        <ReportHeader session={session} />

        {/* ── Verdict hero ── */}
        {verdict ? (
          <>
            <VerdictHero verdict={verdict} isRo={isRo} t={t} />

            {summary && <ActionCallout summary={summary} isRo={isRo} accent={accent} t={t} />}

            <InputPreview session={session} t={t} />

            {summary?.key_findings?.length ? (
              <KeyFindingsList findings={summary.key_findings} isRo={isRo} t={t} />
            ) : null}

            <ScoreBreakdown verdict={verdict} t={t} />

            {/* Explanation (legacy fallback if no verdict_summary) */}
            {!summary && (verdict.explanation_ro || verdict.explanation_en) && (
              <View style={styles.section}>
                <Text style={styles.sectionSubtitle}>{t('analysis.pdf.explanationLabel')}</Text>
                <Text style={styles.inputContent}>
                  {isRo ? (verdict.explanation_ro || verdict.explanation_en) : (verdict.explanation_en || verdict.explanation_ro)}
                </Text>
              </View>
            )}
          </>
        ) : (
          <Text style={styles.textMuted}>{t('analysis.pdf.verdictUnavailable')}</Text>
        )}

        {/* ── Component sections — each starts on new page via `break` ── */}
        {session.techniques && <TechniquesSection data={session.techniques} isRo={isRo} techDefs={techDefs} t={t} />}
        {session.claims && <ClaimsSection data={session.claims} isRo={isRo} t={t} />}
        {session.ai_tampered && <AiTamperedSection data={session.ai_tampered} isRo={isRo} t={t} />}
        {session.source_assessment && <SourceSection data={session.source_assessment} t={t} />}

        {/* ── Footer meta ── */}
        <View style={styles.section} wrap={false}>
          <Text style={styles.sectionSubtitle}>{t('analysis.pdf.metadataLabel')}</Text>
          <Text style={styles.cardMeta}>{t('analysis.pdf.sessionLabel')}: {session.session_id}</Text>
          <Text style={styles.cardMeta}>{t('analysis.pdf.startedLabel')}: {fmtDate(session.started_at)}</Text>
          <Text style={styles.cardMeta}>{t('analysis.pdf.completedLabel')}: {fmtDate(session.completed_at)}</Text>
          <Text style={styles.cardMeta}>{t('analysis.pdf.totalDurationLabel')}: {fmtDuration(session.total_duration_ms)}</Text>
          <Text style={styles.cardMeta}>{t('analysis.pdf.componentsLabel')}: {session.components_run.join(', ') || '—'}</Text>
        </View>

        <ReportFooter />
      </Page>
    </Document>
  );
};

export default AnalysisReportPDF;
